import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertOctagon, Factory, Lightbulb, MonitorSmartphone, Package, Users } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { LiveStationsTab } from '@/components/production/LiveStationsTab';
import { PeopleTab } from '@/components/production/PeopleTab';
import { DefectsTab } from '@/components/production/DefectsTab';
import { FeedbackTab } from '@/components/production/FeedbackTab';
import { InstructionsTab } from '@/components/production/InstructionsTab';
import { CheckinsTab } from '@/components/production/CheckinsTab';
import { HistoryTab } from '@/components/production/HistoryTab';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { useOpenSessions, useRecentUnits, useWorkDefects, useWorkNotes, useWorkRealtime } from '@/features/workMode/hooks';
import { useWorkLookups } from '@/features/workMode/useLookups';

const TABS = ['live', 'people', 'defects', 'feedback', 'instructions', 'checkins', 'history'] as const;
type Tab = (typeof TABS)[number];

/**
 * Manufacturing → Workstations. The manager side of Work Mode: what every
 * bench is doing right now, who can sign in, what went wrong, what the
 * floor is asking for, the illustrated instructions and the history.
 * (Manufacturing → Production is the production-forms ledger.)
 */
export function WorkstationsPage() {
  const { role } = useAuth();
  const canManage = canWrite(role, 'manufacturing');
  const lookups = useWorkLookups();
  const [params, setParams] = useSearchParams();
  const tab: Tab = (TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as Tab) : 'live';

  useWorkRealtime(true);

  const { data: sessions = [], isLoading: sessionsLoading } = useOpenSessions();
  const { data: units = [], isLoading: unitsLoading } = useRecentUnits(500);
  const { data: openDefects = [] } = useWorkDefects('open');
  const { data: openNotes = [] } = useWorkNotes('open');

  const today = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    let good = 0;
    let scrap = 0;
    const people = new Set<string>();
    for (const u of units) {
      if (Date.parse(u.completed_at) < start.getTime()) continue;
      if (u.outcome === 'good') good += 1;
      else scrap += 1;
      people.add(u.user_id);
    }
    return { good, scrap, people: people.size };
  }, [units]);

  const setTab = (next: string) => {
    const p = new URLSearchParams(params);
    if (next === 'live') p.delete('tab');
    else p.set('tab', next);
    if (next !== 'instructions') p.delete('set');
    setParams(p, { replace: true });
  };

  const safetyOpen = openNotes.filter((n) => n.kind === 'safety').length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Manufacturing / Workstations"
        title="Workstations"
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline">
              <Link to="/digital-company">
                <Factory className="h-4 w-4" />
                Digital Company
              </Link>
            </Button>
            <Button asChild>
              <Link to="/work">
                <MonitorSmartphone className="h-4 w-4" />
                Open Work Mode
              </Link>
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Working now"
          value={String(sessions.length)}
          hint={sessions.length ? `${sessions.length} open shift${sessions.length === 1 ? '' : 's'}` : 'No one signed in'}
          loading={sessionsLoading}
          icon={<Users className="h-4 w-4" />}
          tone="positive"
        />
        <MetricCard
          label="Units today"
          value={String(today.good)}
          hint={today.people ? `${today.people} ${today.people === 1 ? 'person' : 'people'} building` : 'Nothing logged yet today'}
          loading={unitsLoading}
          icon={<Package className="h-4 w-4" />}
        />
        <MetricCard
          label="Scrapped today"
          value={String(today.scrap)}
          hint={today.good + today.scrap ? `${((today.scrap / (today.good + today.scrap)) * 100).toFixed(1)}% of units` : undefined}
          loading={unitsLoading}
          icon={<AlertOctagon className="h-4 w-4" />}
          tone={today.scrap > 0 ? 'negative' : 'neutral'}
        />
        <MetricCard
          label="Waiting on you"
          value={String(openDefects.length + openNotes.length)}
          hint={`${openDefects.length} defect${openDefects.length === 1 ? '' : 's'} · ${openNotes.length} note${openNotes.length === 1 ? '' : 's'}${safetyOpen ? ` · ${safetyOpen} safety` : ''}`}
          icon={<Lightbulb className="h-4 w-4" />}
          tone={safetyOpen ? 'negative' : openDefects.length + openNotes.length ? 'info' : 'neutral'}
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap justify-start">
          <TabsTrigger value="live">Live</TabsTrigger>
          <TabsTrigger value="people">People</TabsTrigger>
          <TabsTrigger value="defects">
            Defects
            {openDefects.length > 0 && <Count n={openDefects.length} alert />}
          </TabsTrigger>
          <TabsTrigger value="feedback">
            Feedback
            {openNotes.length > 0 && <Count n={openNotes.length} alert={safetyOpen > 0} />}
          </TabsTrigger>
          <TabsTrigger value="instructions">Instructions</TabsTrigger>
          <TabsTrigger value="checkins">Check-ins</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="live">
          <LiveStationsTab lookups={lookups} canManage={canManage} />
        </TabsContent>
        <TabsContent value="people">
          <PeopleTab lookups={lookups} canManage={canManage} />
        </TabsContent>
        <TabsContent value="defects">
          <DefectsTab lookups={lookups} canManage={canManage} />
        </TabsContent>
        <TabsContent value="feedback">
          <FeedbackTab lookups={lookups} canManage={canManage} />
        </TabsContent>
        <TabsContent value="instructions">
          <InstructionsTab lookups={lookups} canManage={canManage} />
        </TabsContent>
        <TabsContent value="checkins">
          <CheckinsTab lookups={lookups} />
        </TabsContent>
        <TabsContent value="history">
          <HistoryTab lookups={lookups} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Count({ n, alert }: { n: number; alert?: boolean }) {
  return (
    <span
      className={
        alert
          ? 'ml-1.5 rounded-full bg-fresh-salmon/40 px-1.5 text-[10px] font-medium tabular-nums text-fresh-salmon-dark'
          : 'ml-1.5 rounded-full bg-secondary px-1.5 text-[10px] font-medium tabular-nums text-muted-foreground'
      }
    >
      {n}
    </span>
  );
}
